import { ReactNode } from 'react'

interface MainPaneProps {
  title: ReactNode
  headerActions?: ReactNode
  className?: string
  children: ReactNode
  // Header theming
  headerClassName?: string
  headerInnerClassName?: string
  // Details pane control
  detailsOpen?: boolean
  detailsTitle?: string | ReactNode
  detailsPane?: ReactNode
  onCloseDetails?: () => void
  detailsClassName?: string
}

export default function MainPane({
  title,
  headerActions,
  className,
  children,
  headerClassName,
  headerInnerClassName,
  detailsOpen = false,
  detailsTitle,
  detailsPane,
  onCloseDetails,
  detailsClassName,
}: MainPaneProps) {
  const headerClasses = headerClassName || 'bg-white border-gray-200'
  const headerInnerClasses = headerInnerClassName || 'px-5 py-3'

  return (
    <div className={`flex flex-col h-full relative ${className || ''}`}>
      {/* Header - Fixed */}
      <div className={`border-b shrink-0 ${headerClasses}`}>
        <div className={`${headerInnerClasses} w-full`}>
          <div className="flex items-center justify-between gap-3 w-full">
            <div className="min-w-0 flex-1">
                {typeof title === 'string' ? (
                  <h2 className="text-xl font-semibold text-gray-900 truncate">{title}</h2>
                ) : (
                  title
                )}
            </div>
            {headerActions && (
              <div className="flex items-center gap-2 shrink-0">
                {headerActions}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Body + optional split details pane */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Main content; shrinks to half when details open on lg+ */}
        <div className={`${detailsOpen ? 'w-full lg:w-1/2' : 'w-full'} overflow-auto`}> 
          {children}
        </div>

        {/* Right details pane (lg+) */}
        {detailsOpen && (
          <aside className={`hidden lg:flex w-1/2 border-l border-gray-200 bg-white flex-col ${detailsClassName || ''}`}>
            <div className="px-5 py-3 flex items-center justify-between border-b shrink-0">
              <h3 className="text-base font-semibold text-gray-900">{detailsTitle}</h3>
              {onCloseDetails && (
                <button className="p-2 text-gray-500 hover:text-gray-700" onClick={onCloseDetails} aria-label="Close details">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                </button>
              )}
            </div>
            <div className="px-5 py-4 overflow-auto flex-1 min-h-0">
              {detailsPane}
            </div>
          </aside>
        )}
      </div>
    </div>
  )
} 